import React, { useState, useEffect, useRef } from 'react';
// Load leaflet CSS once
if (typeof document !== 'undefined' && !document.getElementById('leaflet-css')) {
  const link = document.createElement('link');
  link.id = 'leaflet-css';
  link.rel = 'stylesheet';
  link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  document.head.appendChild(link);
}
import {
  Modal, View, Text, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, Pressable, Animated, Easing,
} from 'react-native';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
const L = require('leaflet'); // eslint-disable-line
type LatLngExpression = [number, number];

const LeafletMap = MapContainer as any;
const LeafletTile = TileLayer as any;
const LeafletMarker = Marker as any;
const LeafletPopup = Popup as any;
const LeafletPolyline = Polyline as any;
import { collection, onSnapshot, doc, updateDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const ORDER_COLLECTION = 'zapee_restaurant_orders';
const PARTNER_COLLECTION = 'zapee_delivery_partners'; // newer rider schema

// Fix leaflet default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const restaurantIcon = L.divIcon({
  html: `<div style="background:#32a852;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`,
  iconSize: [32, 32], iconAnchor: [16, 32], className: '',
});
const userIcon = L.divIcon({
  html: `<div style="background:#0288d1;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`,
  iconSize: [32, 32], iconAnchor: [16, 32], className: '',
});
const dpIcon = L.divIcon({
  html: `<div style="background:#f59e0b;width:36px;height:36px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 10px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center"><span class="material-icons-round" style="font-size:20px;color:#fff;line-height:1">delivery_dining</span></div>`,
  iconSize: [36, 36], iconAnchor: [18, 18], className: '',
});

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function fmtDist(km: number): string {
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
}
function bezierPoints(sLat: number, sLng: number, eLat: number, eLng: number, steps = 80): [number, number][] {
  const midLat = (sLat + eLat) / 2, midLng = (sLng + eLng) / 2;
  const dist = Math.sqrt((eLat - sLat) ** 2 + (eLng - sLng) ** 2);
  const cpLat = midLat + dist * 0.35, cpLng = midLng;
  const pts: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    pts.push([(1 - t) ** 2 * sLat + 2 * (1 - t) * t * cpLat + t ** 2 * eLat, (1 - t) ** 2 * sLng + 2 * (1 - t) * t * cpLng + t ** 2 * eLng]);
  }
  return pts;
}

function FitBounds({ positions }: { positions: LatLngExpression[] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length >= 2) map.fitBounds(L.latLngBounds(positions), { padding: [50, 50] });
  }, [map, positions]);
  return null;
}

// Rider doc shape from `zapee_delivery_partners`
interface Rider {
  docId: string;
  partnerId?: string;
  name?: string;
  phone?: string;
  active?: boolean;      // Online / Offline
  available?: boolean;   // Free / Unavailable
  currentOrderId?: string; // In a trip
  currentLocation?: { latitude?: number; longitude?: number; updatedAt?: any };
}

// Match an order's deliveryPartnerId against a rider (partnerId or docId)
const riderMatches = (r: Rider, id?: string) => !!id && (r.partnerId === id || r.docId === id);

interface OrderLite {
  id: string;
  orderNumber?: string[];
  deliveryPartnerId?: string;
  sellerLocation?: { name?: string; address?: string; latitude?: number; longitude?: number };
  userLocation?: { name?: string; address?: string; latitude?: number; longitude?: number };
}

interface Props {
  visible: boolean;
  order: OrderLite | null;
  onClose: () => void;
}

const RestaurantOrderMapModal: React.FC<Props> = ({ visible, order, onClose }) => {
  const [partners, setPartners] = useState<Rider[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [conflictOrder, setConflictOrder] = useState<OrderLite | null>(null);
  const [conflictRider, setConflictRider] = useState<Rider | null>(null);
  const [showConflict, setShowConflict] = useState(false);

  const restaurantLat = order?.sellerLocation?.latitude;
  const restaurantLng = order?.sellerLocation?.longitude;
  const userLat = order?.userLocation?.latitude;
  const userLng = order?.userLocation?.longitude;
  const hasLocations = !!(restaurantLat && restaurantLng && userLat && userLng);
  const curvedPath = hasLocations ? bezierPoints(restaurantLat!, restaurantLng!, userLat!, userLng!) : [];

  const assignedRider = partners.find(r => riderMatches(r, order?.deliveryPartnerId)) || null;
  const dpLoc = assignedRider?.currentLocation;
  const allPositions: [number, number][] = [
    ...(restaurantLat && restaurantLng ? [[restaurantLat, restaurantLng] as [number, number]] : []),
    ...(userLat && userLng ? [[userLat, userLng] as [number, number]] : []),
    ...(dpLoc?.latitude && dpLoc?.longitude ? [[dpLoc.latitude, dpLoc.longitude] as [number, number]] : []),
  ];

  // Slide in from the right
  const drawerX = useRef(new Animated.Value(1200)).current;
  useEffect(() => {
    if (visible) {
      drawerX.setValue(1200);
      Animated.timing(drawerX, { toValue: 0, duration: 280, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [visible, drawerX]);

  // Live partners from the new collection
  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    const unsub = onSnapshot(collection(db, PARTNER_COLLECTION), (snap) => {
      setPartners(snap.docs.map(d => ({ docId: d.id, ...(d.data() as any) })) as Rider[]);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [visible]);

  const sortedPartners = partners
    .map(r => {
      const lat = r.currentLocation?.latitude, lon = r.currentLocation?.longitude;
      const dist = lat != null && lon != null && restaurantLat && restaurantLng
        ? haversineKm(lat, lon, restaurantLat, restaurantLng) : null;
      return { rider: r, dist };
    })
    .sort((a, b) => {
      if (a.dist === null && b.dist === null) return 0;
      if (a.dist === null) return 1;
      if (b.dist === null) return -1;
      return a.dist - b.dist;
    });

  const doAssign = async (riderId: string) => {
    if (!order?.id) return;
    await updateDoc(doc(db, ORDER_COLLECTION, order.id), { deliveryPartnerId: riderId });
  };

  const handleAssign = async (rider: Rider) => {
    const riderId = rider.partnerId ?? rider.docId;
    if (!order?.id || !riderId) return;
    setAssigning(true);
    try {
      // Conflict: this rider already has another active order
      const snap = await getDocs(query(
        collection(db, ORDER_COLLECTION),
        where('deliveryPartnerId', '==', riderId),
        where('active', '==', true),
      ));
      const existing = snap.docs.find(d => d.id !== order.id);
      if (existing) {
        setConflictOrder({ id: existing.id, ...(existing.data() as any) });
        setConflictRider(rider);
        setShowConflict(true);
        setAssigning(false);
        return;
      }
      await doAssign(riderId);
    } catch (e) { console.error(e); }
    finally { setAssigning(false); }
  };

  const confirmAssignAnyway = async () => {
    const riderId = conflictRider?.partnerId ?? conflictRider?.docId;
    if (!order?.id || !riderId) return;
    setAssigning(true);
    try { await doAssign(riderId); setShowConflict(false); }
    catch (e) { console.error(e); }
    finally { setAssigning(false); }
  };

  const PartnerCard = ({ rider, dist }: { rider: Rider; dist: number | null }) => {
    const isAssigned = riderMatches(rider, order?.deliveryPartnerId);
    const isSelected = selectedId === rider.docId;
    const inTrip = !!rider.currentOrderId;
    return (
      <TouchableOpacity
        style={[styles.partnerCard, isAssigned && styles.partnerCardAssigned, isSelected && styles.partnerCardSelected]}
        onPress={() => setSelectedId(isSelected ? null : rider.docId)}
        activeOpacity={0.85}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
          <View style={{ position: 'relative', marginRight: 8 }}>
            <View style={[styles.partnerAvatar, { backgroundColor: isAssigned ? '#32a852' : isSelected ? '#8B5CF6' : '#e2e8f0' }]}>
              <Text style={[styles.partnerAvatarText, { color: isAssigned || isSelected ? '#fff' : '#475569' }]}>
                {(rider.name ?? 'D').charAt(0).toUpperCase()}
              </Text>
            </View>
            <View style={[styles.onlineDot, { position: 'absolute', bottom: 0, right: 0, backgroundColor: rider.active ? '#22c55e' : '#d1d5db' }]} />
          </View>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <Text style={styles.partnerCardName} numberOfLines={1}>{rider.name ?? 'Partner'}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: rider.active ? '#dcfce7' : '#f1f5f9', borderRadius: 4, paddingHorizontal: 4, paddingVertical: 1 }}>
                <span className="material-icons-round" style={{ fontSize: 7, color: rider.active ? '#16a34a' : '#9ca3af', lineHeight: 1, marginRight: 2 }}>circle</span>
                <Text style={{ fontSize: 9, fontWeight: '700', color: rider.active ? '#16a34a' : '#9ca3af' }}>{rider.active ? 'Online' : 'Offline'}</Text>
              </View>
            </View>
            <Text style={styles.partnerCardPhone} numberOfLines={1}>{rider.phone ?? '—'}</Text>
          </View>
          {dist !== null && (
            <View style={[styles.distBadge, { backgroundColor: dist < 3 ? '#dcfce7' : dist < 8 ? '#fef9c3' : '#fee2e2' }]}>
              <Text style={[styles.distBadgeText, { color: dist < 3 ? '#16a34a' : dist < 8 ? '#ca8a04' : '#dc2626' }]}>{fmtDist(dist)}</Text>
            </View>
          )}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          {isAssigned ? (
            <View style={styles.assignedBadge}>
              <span className="material-icons-round" style={{ fontSize: 11, color: '#16a34a', lineHeight: 1, marginRight: 3 }}>check_circle</span>
              <Text style={styles.assignedBadgeText}>Assigned</Text>
            </View>
          ) : (
            <TouchableOpacity style={styles.assignBtn} onPress={() => handleAssign(rider)} disabled={assigning}>
              <span className="material-icons-round" style={{ fontSize: 12, color: '#fff', lineHeight: 1, marginRight: 3 }}>person_add</span>
              <Text style={styles.assignBtnText}>Assign</Text>
            </TouchableOpacity>
          )}
          {inTrip ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fef3c7', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 }}>
              <span className="material-icons-round" style={{ fontSize: 10, color: '#d97706', lineHeight: 1, marginRight: 3 }}>local_shipping</span>
              <Text style={{ fontSize: 10, fontWeight: '600', color: '#d97706' }}>In Trip</Text>
            </View>
          ) : rider.available ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#eff6ff', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 }}>
              <span className="material-icons-round" style={{ fontSize: 10, color: '#3b82f6', lineHeight: 1, marginRight: 3 }}>check_circle</span>
              <Text style={{ fontSize: 10, fontWeight: '600', color: '#3b82f6' }}>Free</Text>
            </View>
          ) : (
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#f1f5f9', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 }}>
              <span className="material-icons-round" style={{ fontSize: 10, color: '#9ca3af', lineHeight: 1, marginRight: 3 }}>do_not_disturb_on</span>
              <Text style={{ fontSize: 10, fontWeight: '600', color: '#9ca3af' }}>Unavailable</Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={onClose} />
        <Animated.View style={[styles.sheet, { transform: [{ translateX: drawerX }] }]}>

          {/* Header */}
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>Order Map</Text>
              <Text style={styles.subtitle} numberOfLines={1}>{order?.orderNumber?.[0] ?? ''}</Text>
            </View>
            <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
              <span className="material-icons-round" style={{ fontSize: 22, color: '#6B7280', lineHeight: 1 }}>close</span>
            </TouchableOpacity>
          </View>

          {/* Vertical scrollable body */}
          <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.body} showsVerticalScrollIndicator={false}>

            {/* SECTION 1: Order Route */}
            <View style={styles.col}>
              <View style={styles.colHeader}>
                <span className="material-icons-round" style={{ fontSize: 14, color: '#3B82F6', lineHeight: 1, marginRight: 5 }}>map</span>
                <Text style={styles.colTitle}>Order Route</Text>
                {assignedRider && (
                  <View style={[styles.dpLiveBadge, { backgroundColor: assignedRider.active ? '#dcfce7' : '#f3f4f6', marginLeft: 'auto', flexDirection: 'row', alignItems: 'center' }]}>
                    <span className="material-icons-round" style={{ fontSize: 8, color: assignedRider.active ? '#16a34a' : '#9CA3AF', lineHeight: 1, marginRight: 3 }}>circle</span>
                    <Text style={[styles.dpLiveBadgeText, { color: assignedRider.active ? '#16a34a' : '#9CA3AF' }]}>{assignedRider.active ? 'Live' : 'Offline'}</Text>
                  </View>
                )}
              </View>
              <View style={styles.mapWrap}>
                {hasLocations ? (
                  <LeafletMap center={[restaurantLat!, restaurantLng!]} zoom={13} style={{ width: '100%', height: '100%' }} zoomControl>
                    <LeafletTile url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                    <FitBounds positions={allPositions} />
                    <LeafletMarker position={[restaurantLat!, restaurantLng!]} icon={restaurantIcon}>
                      <LeafletPopup><strong>Restaurant</strong><br />{order?.sellerLocation?.name}</LeafletPopup>
                    </LeafletMarker>
                    <LeafletMarker position={[userLat!, userLng!]} icon={userIcon}>
                      <LeafletPopup><strong>Customer</strong><br />{order?.userLocation?.name}<br />{order?.userLocation?.address}</LeafletPopup>
                    </LeafletMarker>
                    {dpLoc?.latitude && dpLoc?.longitude && (
                      <LeafletMarker position={[dpLoc.latitude, dpLoc.longitude]} icon={dpIcon}>
                        <LeafletPopup><strong>{assignedRider?.name ?? 'Partner'}</strong><br />{assignedRider?.phone}</LeafletPopup>
                      </LeafletMarker>
                    )}
                    {curvedPath.length > 0 && (
                      <LeafletPolyline positions={curvedPath} pathOptions={{ color: '#0288d1', weight: 3, dashArray: '6, 8', opacity: 0.85 }} />
                    )}
                  </LeafletMap>
                ) : (
                  <View style={styles.noLocation}><Text style={styles.noLocationText}>No location data</Text></View>
                )}
              </View>
              {/* Info cards */}
              <View style={styles.infoCards}>
                <View style={[styles.infoCard, { borderTopColor: '#22c55e' }]}>
                  <View style={styles.infoCardIcon}><span className="material-icons-round" style={{ fontSize: 16, color: '#16a34a', lineHeight: 1 }}>storefront</span></View>
                  <Text style={styles.infoCardLabel}>Restaurant</Text>
                  <Text style={styles.infoCardName} numberOfLines={2}>{order?.sellerLocation?.name ?? '—'}</Text>
                  {order?.sellerLocation?.address ? <Text style={styles.infoCardSub} numberOfLines={1}>{order.sellerLocation.address}</Text> : null}
                </View>
                <View style={[styles.infoCard, { borderTopColor: '#3b82f6' }]}>
                  <View style={styles.infoCardIcon}><span className="material-icons-round" style={{ fontSize: 16, color: '#2563eb', lineHeight: 1 }}>person_pin_circle</span></View>
                  <Text style={styles.infoCardLabel}>Customer</Text>
                  <Text style={styles.infoCardName} numberOfLines={2}>{order?.userLocation?.name ?? '—'}</Text>
                  {order?.userLocation?.address ? <Text style={styles.infoCardSub} numberOfLines={1}>{order.userLocation.address}</Text> : null}
                </View>
                <View style={[styles.infoCard, { borderTopColor: '#f59e0b' }]}>
                  <View style={styles.infoCardIcon}><span className="material-icons-round" style={{ fontSize: 16, color: '#d97706', lineHeight: 1 }}>delivery_dining</span></View>
                  <Text style={styles.infoCardLabel}>Partner</Text>
                  {assignedRider ? (
                    <>
                      <Text style={styles.infoCardName} numberOfLines={1}>{assignedRider.name ?? 'Partner'}</Text>
                      <Text style={styles.infoCardSub} numberOfLines={1}>{assignedRider.phone}</Text>
                    </>
                  ) : (
                    <Text style={styles.infoCardSub}>Not assigned</Text>
                  )}
                </View>
              </View>
            </View>

            {/* SECTION 2: Nearby Partners */}
            <View style={[styles.col, styles.colMiddle]}>
              <View style={styles.colHeader}>
                <span className="material-icons-round" style={{ fontSize: 14, color: '#8B5CF6', lineHeight: 1, marginRight: 5 }}>delivery_dining</span>
                <Text style={styles.colTitle}>Partners</Text>
                <Text style={styles.sectionCount}>{sortedPartners.length}</Text>
              </View>
              {loading ? (
                <ActivityIndicator color="#8B5CF6" style={{ marginTop: 40 }} />
              ) : sortedPartners.length === 0 ? (
                <Text style={styles.noLocationText}>No partners found</Text>
              ) : (
                <View style={{ gap: 8, paddingBottom: 10 }}>
                  {sortedPartners.map(({ rider, dist }) => (
                    <PartnerCard key={rider.docId} rider={rider} dist={dist} />
                  ))}
                </View>
              )}
            </View>

          </ScrollView>
        </Animated.View>
      </View>

      {/* Conflict modal */}
      <Modal visible={showConflict} transparent animationType="fade" onRequestClose={() => setShowConflict(false)}>
        <Pressable style={styles.conflictOverlay} onPress={() => setShowConflict(false)}>
          <Pressable style={styles.conflictSheet} onPress={() => {}}>
            <View style={styles.conflictHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <span className="material-icons-round" style={{ fontSize: 18, color: '#DC2626', lineHeight: 1 }}>warning</span>
                <Text style={styles.conflictTitle}>Partner Has Active Order</Text>
              </View>
              <TouchableOpacity onPress={() => setShowConflict(false)}>
                <span className="material-icons-round" style={{ fontSize: 20, color: '#6B7280', lineHeight: 1 }}>close</span>
              </TouchableOpacity>
            </View>
            <Text style={styles.conflictBody}>
              <Text style={{ fontWeight: '700' }}>{conflictRider?.name}</Text> is currently delivering order{' '}
              <Text style={{ fontWeight: '700' }}>#{conflictOrder?.orderNumber?.[0]}</Text>.
              {'\n'}Do you still want to assign them?
            </Text>
            <View style={styles.conflictActions}>
              <TouchableOpacity style={styles.conflictCancel} onPress={() => setShowConflict(false)}>
                <Text style={styles.conflictCancelText}>No, Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.conflictConfirm} onPress={confirmAssignAnyway} disabled={assigning}>
                {assigning ? <ActivityIndicator color="#fff" size="small" /> : <Text style={styles.conflictConfirmText}>Yes, Assign Anyway</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </Modal>
  );
};

export default RestaurantOrderMapModal;

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', flexDirection: 'row', justifyContent: 'flex-end' },
  backdrop: { flex: 1, height: '100%' },
  sheet: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderBottomLeftRadius: 20, height: '100%', width: 480, maxWidth: '95%', shadowColor: '#000', shadowOffset: { width: -2, height: 0 }, shadowOpacity: 0.18, shadowRadius: 16, elevation: 14 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 18, paddingTop: 14, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  title: { fontSize: 16, fontWeight: '700', color: '#1e293b' },
  subtitle: { fontSize: 12, color: '#64748B', marginTop: 2 },
  closeBtn: { padding: 6, borderRadius: 8, backgroundColor: '#F1F5F9' },
  body: { flexDirection: 'column', padding: 10, gap: 10 },
  col: { width: '100%' },
  colMiddle: { borderTopWidth: 1, borderColor: '#F1F5F9', paddingTop: 10 },
  colHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  colTitle: { fontSize: 12, fontWeight: '700', color: '#374151' },
  sectionCount: { marginLeft: 6, fontSize: 11, color: '#9CA3AF', fontWeight: '600' },
  dpLiveBadge: { marginLeft: 6, paddingHorizontal: 7, paddingVertical: 2, borderRadius: 20 },
  dpLiveBadgeText: { fontSize: 10, fontWeight: '600' },
  mapWrap: { height: 300, borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: '#E2E8F0' },
  noLocation: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', padding: 16 },
  noLocationText: { color: '#9CA3AF', fontSize: 12, textAlign: 'center', marginTop: 12 },
  infoCards: { flexDirection: 'row', gap: 6, paddingTop: 8 },
  infoCard: { flex: 1, backgroundColor: '#F8FAFC', borderRadius: 8, padding: 8, borderWidth: 1, borderColor: '#E2E8F0', borderTopWidth: 3 },
  infoCardIcon: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center', marginBottom: 4, borderWidth: 1, borderColor: '#E2E8F0' },
  infoCardLabel: { fontSize: 9, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 },
  infoCardName: { fontSize: 11, fontWeight: '700', color: '#1e293b', marginBottom: 1 },
  infoCardSub: { fontSize: 10, color: '#64748B' },
  partnerCard: { backgroundColor: '#F8FAFC', borderRadius: 10, padding: 10, borderWidth: 1.5, borderColor: '#E2E8F0' },
  partnerCardAssigned: { borderColor: '#22c55e', backgroundColor: '#f0fdf4' },
  partnerCardSelected: { borderColor: '#8B5CF6', backgroundColor: '#faf5ff' },
  partnerAvatar: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#e2e8f0', alignItems: 'center', justifyContent: 'center' },
  partnerAvatarText: { fontSize: 15, fontWeight: '700', color: '#475569' },
  onlineDot: { width: 10, height: 10, borderRadius: 5 },
  partnerCardName: { fontSize: 13, fontWeight: '700', color: '#1e293b', marginBottom: 2 },
  partnerCardPhone: { fontSize: 11, color: '#64748B', marginBottom: 6 },
  distBadge: { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 20, marginBottom: 6 },
  distBadgeText: { fontSize: 11, fontWeight: '700' },
  assignedBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#dcfce7', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 20, alignSelf: 'flex-start' },
  assignedBadgeText: { fontSize: 11, fontWeight: '700', color: '#16a34a' },
  assignBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#21808D', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20, alignSelf: 'flex-start' },
  assignBtnText: { fontSize: 11, fontWeight: '700', color: '#fff' },
  conflictOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  conflictSheet: { backgroundColor: '#fff', borderRadius: 16, width: '100%', maxWidth: 400, overflow: 'hidden' },
  conflictHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  conflictTitle: { fontSize: 15, fontWeight: '700', color: '#DC2626' },
  conflictBody: { padding: 16, fontSize: 13, color: '#374151', lineHeight: 20 },
  conflictActions: { flexDirection: 'row', padding: 16, gap: 10, borderTopWidth: 1, borderTopColor: '#F1F5F9' },
  conflictCancel: { flex: 1, paddingVertical: 10, borderRadius: 8, backgroundColor: '#F1F5F9', alignItems: 'center' },
  conflictCancelText: { fontSize: 13, fontWeight: '600', color: '#374151' },
  conflictConfirm: { flex: 1, paddingVertical: 10, borderRadius: 8, backgroundColor: '#DC2626', alignItems: 'center' },
  conflictConfirmText: { fontSize: 13, fontWeight: '700', color: '#fff' },
});
